import isNil from 'lodash/isNil';
import { FC, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CSSObject } from 'styled-components';

import { Icon } from '@spotnana/blocks/src/Icon';
import { Link } from '@spotnana/blocks/src/Link/Link';
import { Typography } from '@spotnana/blocks/src/Typography';
import { margins } from '@spotnana/blocks/src/utils';

import ExpansionPanel from '@spotnana/pixel-react/dist/ExpansionPanel';
import {
  BookingPaymentSource,
  IUserOrgId,
  ListBookingPaymentSourcesRequestType,
  PaymentSourceType,
  invalidateListBookingPaymentSourcesQuery,
  useFeatureFlag,
  useListBookingPaymentSourcesQuery,
  usePaymentSourceInfoText,
  usePaymentSourceTypesAndTabs,
} from 'obt-common';
import { AirInitiateBookingResponse } from 'obt-common/types/api/v1/obt/air/air_intitate_booking_response';
import { BookingWorkflowType } from 'obt-common/types/api/v2/obt/model/booking-workflow-type';
import { CheckoutTravelerInfo } from 'obt-common/types/api/v2/obt/model/checkout-traveler-info';

import { Divider } from '@spotnana/blocks/src/Divider';
import { Radio } from '@spotnana/blocks/src/Radio';
import { Surface } from '@spotnana/blocks/src/Surface';
import { paddings } from '@spotnana/blocks/src/utils/styling/utilityTokens';
import { CheckoutInfoContainer } from 'src/app/flights/shared/CheckoutInfoContainer';
import { Tabs } from '@spotnana/blocks/src/Tabs/Tabs';
import CheckoutSectionLoader from '../CheckoutSectionLoader';
import PaymentMethodErrorAlert from '../PaymentMethodSelector/PaymentMethodErrorAlert';
import AddCardOption from './AddCardOption';
import CardCarousel from './CardCarousel';
import CardOption from './CardOption';
import DelayedInvoicingOption from './DelayedInvoicingOption';
import RewardsProgramOption from './RewardsProgramOption';
import VirtualCardInfo from './VirtualCardInfo';
import useSelectCreatedPaymentSource from './useSelectCreatedPaymentSource';

import {
  direct_billing_instruction_title,
  hold_booking_payment_message,
  info_text,
  payment_element,
  payment_method_disclaimer,
  payment_methods_register_disclaimer,
} from './styles';

const tabLabel = (label: string) => label;
const qantasPaymentOptionsUrl = 'https://www.qantas.com/au/en/business-rewards/info/schedule-of-fees.html';

interface PaymentOptionsProps {
  /**
   * ID of the itinerary
   */
  itineraryId: string;
  /**
   * ID of the itinerary (in case of post booking edit seat)
   */
  seatMapResponseId?: string;
  /**
   * pnrId of pnr (in case of post booking edit seat)
   */
  pnrId?: string;
  /**
   * Type of booking
   */
  bookingType: ListBookingPaymentSourcesRequestType;
  /**
   * Callback fired when selecting payment source
   */
  onPaymentSourceSelect: (selectedPaymentSources: BookingPaymentSource[]) => void;
  /**
   * Array of selected payment source IDs
   */
  selectedPaymentSourceIds: string[];
  /**
   * ID of search
   */
  searchId?: string;
  /**
   * ID of primary traveler
   */
  primaryTravelerId: IUserOrgId;
  /**
   * String that indicates which page this component is used in
   */
  usedIn?: 'ShellPnr' | 'Checkout';
  /**
   * If true, flight will be paid for after itinerary completes
   */
  isPostPaid?: boolean;
  initiateOrderResponse?: AirInitiateBookingResponse | undefined;
  containerStyles?: CSSObject;
  cardStyles?: CSSObject;
  bookingWorkflowType: BookingWorkflowType;
  isTravelerUnregistered?: boolean;
  checkoutTravelerInfo?: CheckoutTravelerInfo;
  showPaymentMethodDisclaimer?: boolean;
  tripId?: string;
  isHoldBooking?: boolean;
  onPaymentAddNewCardClick?: VoidFunction;
}

const PaymentOptions: FC<PaymentOptionsProps> = ({
  itineraryId,
  seatMapResponseId,
  pnrId,
  bookingType,
  onPaymentSourceSelect,
  selectedPaymentSourceIds,
  usedIn = 'Checkout',
  searchId,
  primaryTravelerId,
  isPostPaid,
  initiateOrderResponse,
  containerStyles,
  cardStyles,
  bookingWorkflowType,
  isTravelerUnregistered,
  checkoutTravelerInfo,
  showPaymentMethodDisclaimer = false,
  tripId,
  isHoldBooking,
  onPaymentAddNewCardClick,
}) => {
  const { t: tt } = useTranslation('WEB');

  const isHoldV2Enabled = useFeatureFlag('FE_HOLD_V2');

  const [isCreatedPaymentSourceFetching, setIsCreatedPaymentSourceFetching] = useState(false);

  const isShellPnr = usedIn === 'ShellPnr';

  // TODO: Refactor this to IIFE
  let enabled = false;
  if (isShellPnr) {
    enabled = true;
  }

  if (!isNil(itineraryId) && !isShellPnr) {
    if (bookingType !== ListBookingPaymentSourcesRequestType.AIR) {
      enabled = true;
    } else {
      enabled = !!initiateOrderResponse;
    }
  } else if (seatMapResponseId) {
    enabled = true;
  }

  if (isTravelerUnregistered) {
    enabled = false;
  }

  // TODO (unified payments - revanth) : handle searchId in a generic way
  const { data, isFetching: listPaymentSourceFetching } = useListBookingPaymentSourcesQuery(
    {
      type: bookingType,
      itineraryId,
      seatMapResponseId,
      pnrId,
      searchId,
      userId: primaryTravelerId.userId?.id ?? '',
      initiateBookingResponseId: initiateOrderResponse?.initiateBookingResponseId,
      bookingWorkflowType,
      checkoutTravelerInfo,
      tripId,
    },
    {
      enabled,
    },
  );

  // TODO: refactor this snippet to use a state variable instead of external variables
  const allowAddCard = data?.allowAddCard ?? false;
  const selectAdditionalPaymentSource = data?.selectAdditionalPaymentSource ?? false;
  const paymentSources = useMemo(() => {
    if (!data?.bookingPaymentSources) {
      return [];
    }
    return data.bookingPaymentSources;
  }, [data?.bookingPaymentSources]);

  const {
    tabs,
    selectedPaymentSourceType,
    handlePaymentSourceTypeSelect,
    paymentSourceTypes,
    paymentSourcesWithSelectedType,
    paymentSourcesRequired,
  } = usePaymentSourceTypesAndTabs({
    paymentSources,
    selectedPaymentSourceIds,
    onPaymentSourceSelect,
    tabLabel,
    allowAddCard,
    selectAdditionalPaymentSource,
  });

  const tabsList = Object.entries(tabs).map(([key, value]) => ({ tabKey: key, value, label: value }));

  const isRewardsSourceTypeAvailable = paymentSourceTypes.includes(PaymentSourceType.RewardsProgram);
  const isDelayedInvoicingAvailable = paymentSourceTypes.includes(PaymentSourceType.DelayedInvoicing);
  const isCustomPaymentMethodAvailable = paymentSourceTypes.includes(PaymentSourceType.CustomPaymentMethod);

  const cardsCount = paymentSourcesWithSelectedType.length;

  const handleOnCardCreate = () => {
    invalidateListBookingPaymentSourcesQuery();
    setIsCreatedPaymentSourceFetching(true);
  };

  useSelectCreatedPaymentSource({
    selectedPaymentSourceIds,
    paymentSources,
    isCreatedPaymentSourceFetching,
    onPaymentSourceSelect,
    setIsCreatedPaymentSourceFetching,
  });

  const handlePaymentSourceSelect = (paymentSourceId: string) => {
    const paymentSource = paymentSourcesWithSelectedType.find((source) => source.id === paymentSourceId);
    const selectedPaymentSources = [];
    if (paymentSource) {
      selectedPaymentSources.push(paymentSource);
    }
    if (selectAdditionalPaymentSource && paymentSourcesRequired.length > 0) {
      // to handle United UAPass plus case
      selectedPaymentSources.push(paymentSourcesRequired[0]);
    }
    if (selectedPaymentSources.length > 0) {
      onPaymentSourceSelect(selectedPaymentSources);
    }
  };

  const primaryPaymentSource = paymentSourcesWithSelectedType.find(
    (source) => source.id === selectedPaymentSourceIds[0],
  );

  const infoText = usePaymentSourceInfoText({
    primaryPaymentSource,
    bookingType,
    isPostPaid,
  });

  if (isTravelerUnregistered) {
    return (
      <div style={cardStyles} css={payment_methods_register_disclaimer}>
        <Typography variant="body1">
          {tt('Confirm the guest traveler details to view the eligible payment options')}
        </Typography>
      </div>
    );
  }

  // TODO: refactor these conditional renders because having an if/else
  // condition that returns different JSX outside of the component's
  // render function is not considered good practice in React.
  if (listPaymentSourceFetching) {
    return (
      <div css={margins.y.m1} style={containerStyles}>
        <CheckoutSectionLoader />
      </div>
    );
  }

  if (paymentSources.length === 0 && !allowAddCard) {
    return (
      <div data-testid="payment-options" css={margins.y.m1} style={containerStyles}>
        <Surface
          variant="base"
          css={[paddings.x.extraCozy, paddings.y.extraCozy]}
          border="light"
          borderRadius="outer"
          style={cardStyles}
        >
          <PaymentMethodErrorAlert title={tt('No payment method available')} subtitle="" />
        </Surface>
      </div>
    );
  }

  if (isDelayedInvoicingAvailable) {
    return (
      <div data-testid="payment-options" css={margins.y.m1} style={containerStyles}>
        <DelayedInvoicingOption />
      </div>
    );
  }

  /*
   * TODO: refactor the component to utilize Layout from blocks, remove  explicit styling
   */

  return (
    <div data-testid="payment-options" css={margins.y.m1} style={containerStyles}>
      <CheckoutInfoContainer style={cardStyles} css={[paddings.x.tight, paddings.y.tight]}>
        {showPaymentMethodDisclaimer && (
          <div css={[payment_element, payment_method_disclaimer]}>
            <Typography variant="body2" color="secondary">
              <Trans t={tt}>
                For other{' '}
                <Link href={qantasPaymentOptionsUrl} target="_blank">
                  payment options
                </Link>{' '}
                a percentage based fee applies, capped at a maximum per passenger of AUD $22.00. Prices are not
                guaranteed until payment is made.
              </Trans>
            </Typography>
          </div>
        )}
        {isRewardsSourceTypeAvailable && (
          <div css={payment_element}>
            <Radio
              label={tt('Pay through card')}
              value={PaymentSourceType.Card}
              checked={selectedPaymentSourceType !== PaymentSourceType.RewardsProgram}
              onChange={() => {
                const sourceType = isCustomPaymentMethodAvailable
                  ? PaymentSourceType.CustomPaymentMethod
                  : PaymentSourceType.Card;
                handlePaymentSourceTypeSelect(sourceType);
              }}
              name="paymentMethod"
            />
          </div>
        )}
        <ExpansionPanel
          expand={isRewardsSourceTypeAvailable ? selectedPaymentSourceType !== PaymentSourceType.RewardsProgram : true}
        >
          <Tabs
            orientation="horizontal"
            items={tabsList}
            value={tabs[selectedPaymentSourceType]}
            onChange={(_, value: string) =>
              handlePaymentSourceTypeSelect(tabsList.find((tab) => tab.value === value)?.tabKey as PaymentSourceType)
            }
            tabListId="payment-method-selector"
          />
          <div css={payment_element}>
            {PaymentSourceType.Card === selectedPaymentSourceType && (
              <CardCarousel
                cardsCount={cardsCount}
                actions={
                  allowAddCard && (
                    <AddCardOption
                      userOrgId={primaryTravelerId}
                      onCardCreate={handleOnCardCreate}
                      cardCount={cardsCount}
                      onPaymentAddNewCardClick={onPaymentAddNewCardClick}
                    />
                  )
                }
              >
                {paymentSourcesWithSelectedType.map((paymentSource) => (
                  <CardOption
                    key={paymentSource.id}
                    paymentSource={paymentSource}
                    onPaymentSourceSelect={handlePaymentSourceSelect}
                    selectedPaymentSourceId={selectedPaymentSourceIds[0]}
                  />
                ))}
              </CardCarousel>
            )}

            {PaymentSourceType.VirtualCard === selectedPaymentSourceType && (
              <>
                <VirtualCardInfo bookingType={bookingType} />
                <CardCarousel cardsCount={cardsCount}>
                  {paymentSourcesWithSelectedType.map((paymentSource) => (
                    <CardOption
                      key={paymentSource.id}
                      paymentSource={paymentSource}
                      onPaymentSourceSelect={handlePaymentSourceSelect}
                      selectedPaymentSourceId={selectedPaymentSourceIds[0]}
                    />
                  ))}
                </CardCarousel>
              </>
            )}

            {PaymentSourceType.VendorProgramPayment === selectedPaymentSourceType && (
              <>
                <div css={direct_billing_instruction_title}>
                  <Typography variant="body2" kind="semibold">
                    {tt('Direct billing instructions')}
                  </Typography>
                </div>

                <Typography variant="body2">
                  {tt(
                    'You will need to go to the car vendor counter before picking up your vehicle. If you have an applicable loyalty or program number, please make sure to present it at pickup.',
                  )}
                </Typography>

                <CardCarousel cardsCount={cardsCount}>
                  {paymentSourcesWithSelectedType.map((paymentSource) => (
                    <CardOption
                      key={paymentSource.id}
                      paymentSource={paymentSource}
                      onPaymentSourceSelect={handlePaymentSourceSelect}
                      selectedPaymentSourceId={selectedPaymentSourceIds[0]}
                    />
                  ))}
                </CardCarousel>
              </>
            )}

            {PaymentSourceType.CustomPaymentMethod === selectedPaymentSourceType && (
              <div css={margins.y.m1point5} style={containerStyles}>
                {paymentSourcesWithSelectedType.map((paymentSource) => (
                  <CardOption
                    key={paymentSource.id}
                    paymentSource={paymentSource}
                    onPaymentSourceSelect={handlePaymentSourceSelect}
                    selectedPaymentSourceId={selectedPaymentSourceIds[0]}
                  />
                ))}
              </div>
            )}
          </div>
        </ExpansionPanel>

        {isRewardsSourceTypeAvailable && (
          <>
            <Divider />
            <div css={payment_element}>
              <RewardsProgramOption
                paymentSource={paymentSources.find(
                  (paymentSource) => paymentSource.type === PaymentSourceType.RewardsProgram,
                )}
                onPaymentSourceTypeSelect={handlePaymentSourceTypeSelect}
                selectedPaymentSourceId={selectedPaymentSourceIds[0]}
              />
            </div>
          </>
        )}

        {paymentSourcesRequired.length > 0 && selectAdditionalPaymentSource && (
          <>
            <Divider />
            <div css={payment_element}>
              <Typography variant="body1" kind="semibold" css={[margins.yb.point75, margins.xl.half]}>
                {tt('United PassPlus')}
              </Typography>

              <CardOption
                paymentSource={paymentSourcesRequired[0]}
                selectedPaymentSourceId={selectedPaymentSourceIds[1]}
              />
            </div>
          </>
        )}

        {infoText && (
          <div css={[payment_element, info_text]}>
            <Icon name="Warning" />
            <Typography variant="body2">{infoText}</Typography>
          </div>
        )}

        {isHoldV2Enabled && isHoldBooking && (
          <div css={hold_booking_payment_message}>
            <Icon name="InfoCircle" width={20} height={20} />
            <Typography variant="body2">
              {tt(
                'Select a payment method now or change this at the time of booking completion. You will not be charged yet.',
              )}
            </Typography>
          </div>
        )}
      </CheckoutInfoContainer>
    </div>
  );
};

PaymentOptions.defaultProps = {
  selectedPaymentSourceIds: [],
};

export default PaymentOptions;
